import React, { useState } from 'react';
import { useVersionPoll, useBackendVersion } from '../../utils';
import * as s from './styled-version-update-banner';

// Renderizado globalmente em App.js. Dois sinais alimentam este
// banner:
//   1. useVersionPoll       — comparou APP_VERSION com /version.json
//                             (build estatica do nginx).
//   2. useBackendVersion    — comparou major(APP_VERSION) com
//                             minFrontendVersion vindo de /api/version.
//
// Severidades:
//   - 'critical' (vermelho, sem dismiss): backend exige major maior.
//     Operador esta rodando codigo que talvez nao se entenda mais com
//     a API. Atualizar e mandatorio.
//   - 'info'     (cyan, dismissivel): build mais recente disponivel
//     no nginx, mas FE atual ainda funciona. Operador escolhe quando.
//
// Prioridade: critical > info. So um banner aparece por vez —
// stacking visual e ruim e duplica acao (ambos pedem reload).

const IRefresh = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
    <path d="M21 3v5h-5" />
  </svg>
);

const IClose = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const VersionUpdateBanner = () => {
  const { update, reload: reloadFromPoll } = useVersionPoll();
  const {
    incompatible,
    minFrontendVersion,
    reload: reloadFromBackend,
  } = useBackendVersion();
  const [dismissed, setDismissed] = useState(false);

  // Critical wins. Tambem ignora flag dismissed — operador nao pode
  // dispensar incompat.
  if (incompatible) {
    return (
      <s.Banner role="alert" aria-live="assertive" $severity="critical">
        <s.Dot $severity="critical" />
        <s.Text $severity="critical">
          <span className="label">Versão obsoleta — atualize</span>
          {minFrontendVersion && <strong>min v{minFrontendVersion}</strong>}
        </s.Text>
        <s.ReloadButton
          type="button"
          onClick={reloadFromBackend}
          $severity="critical"
        >
          {IRefresh}
          Atualizar agora
        </s.ReloadButton>
        {/* Sem DismissButton: critical nao se dispensa */}
      </s.Banner>
    );
  }

  if (!update || dismissed) return null;

  // Rotulo conforme o tipo de atualizacao:
  //  - versionChanged: o numero da versao subiu (release) -> "Nova versao"
  //  - so o build mudou (push em main, mesma versao) -> "Atualizacao
  //    disponivel" (nao implica bump de versao, evita mostrar "nova
  //    versao v1.0.0" quando o operador ja esta na 1.0.0)
  return (
    <s.Banner role="status" aria-live="polite" $severity="info">
      <s.Dot $severity="info" />
      <s.Text $severity="info">
        <span className="label">
          {update.versionChanged
            ? 'Nova versão disponível'
            : 'Atualização disponível'}
        </span>
        {/* Sempre mostra o numero da versao (v X.Y.Z). O commitSha
            (que aparecia solto antes) confundia — parecia "nao ter
            versao". Fica so no rodape/version.json pra debug. */}
        <strong>v{update.version}</strong>
      </s.Text>
      <s.ReloadButton
        type="button"
        onClick={reloadFromPoll}
        $severity="info"
      >
        {IRefresh}
        Atualizar
      </s.ReloadButton>
      <s.DismissButton
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dispensar aviso"
        title="Dispensar (reaparece no proximo check)"
      >
        {IClose}
      </s.DismissButton>
    </s.Banner>
  );
};

export default VersionUpdateBanner;
